import { deviceTokenStorage } from './deviceToken'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

const TOKEN_KEY = 'seven_analytics_token'
const REFRESH_TOKEN_KEY = 'seven_analytics_refresh_token'

export class ApiError extends Error {
  status: number
  code?: string
  details?: any

  constructor(message: string, status: number, code?: string, details?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  remove: () => localStorage.removeItem(TOKEN_KEY),
  
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token),
  removeRefreshToken: () => localStorage.removeItem(REFRESH_TOKEN_KEY),
}

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

interface ExtendedRequestInit extends RequestInit {
  responseType?: 'json' | 'blob'
}

async function request<T>(path: string, options?: ExtendedRequestInit): Promise<T> {
  const token = tokenStorage.get()
  const deviceToken = deviceTokenStorage.get()

  const isFormData = options?.body instanceof FormData
  const headers: Record<string, any> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-Device-Token': deviceToken,
    ...options?.headers,
  }

  // Remove Content-Type if undefined is passed to allow browser to set boundary for FormData
  if ('Content-Type' in headers && headers['Content-Type'] === undefined) {
    delete headers['Content-Type']
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers,
    })
  } catch (err) {
    const errorMsg = (err as Error)?.message ?? ''
    const isNetworkError =
      err instanceof TypeError ||
      errorMsg.includes('Failed to fetch') ||
      errorMsg.includes('NetworkError')

    if (isNetworkError) {
      window.dispatchEvent(new CustomEvent('app:offline'))
      console.warn(`[API Connection Status] Offline: Failed to fetch ${path}`)
      throw new Error('Não foi possível conectar à internet. Verifique sua conexão e tente novamente.')
    }
    throw err
  }

  if (!response.ok) {
    if (!navigator.onLine) {
      window.dispatchEvent(new CustomEvent('app:offline'))
      throw new Error('Não foi possível conectar à internet. Verifique sua conexão e tente novamente.')
    }

    const error = await response.json().catch(() => ({ message: response.statusText }))
    
    if (
      response.status === 503 ||
      (error.message && (
        error.message.includes('indisponível') ||
        error.message.includes('conexão') ||
        error.message.includes('Erro interno ao validar assinatura')
      ))
    ) {
      throw new Error('Não foi possível conectar à internet. Verifique sua conexão e tente novamente.')
    }

    // Dispara evento global se for erro de plano inativo
    if (response.status === 402 && error.code === 'NO_ACTIVE_PLAN') {
      window.dispatchEvent(new CustomEvent('plan:required'))
    }

    // Se o token estiver expirado ou inválido (401)
    if (
      response.status === 401 &&
      path !== '/auth/refresh' &&
      path !== '/auth/login' &&
      window.location.pathname !== '/login'
    ) {
      const refreshToken = tokenStorage.getRefreshToken()
      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true
          try {
            // Chamada direta via fetch para evitar interceptar a si mesma
            const refreshRes = await fetch(`${BASE_URL}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken })
            })
            
            if (!refreshRes.ok) {
              throw new Error('Refresh failed')
            }
            
            const data = await refreshRes.json() as { token: string; refreshToken: string }
            tokenStorage.set(data.token)
            tokenStorage.setRefreshToken(data.refreshToken)
            
            isRefreshing = false
            onRefreshed(data.token)
            
            // Retry the original request
            const newHeaders = {
              ...headers,
              Authorization: `Bearer ${data.token}`
            }
            return request<T>(path, { ...options, headers: newHeaders })
          } catch (refreshErr) {
            isRefreshing = false
            refreshSubscribers = [] // clear queue
            tokenStorage.remove()
            tokenStorage.removeRefreshToken()
            window.location.href = '/login?expired=true'
            throw refreshErr
          }
        } else {
          // Wait for the current refresh to complete
          return new Promise<T>((resolve) => {
            subscribeTokenRefresh((newToken) => {
              const newHeaders = {
                ...headers,
                Authorization: `Bearer ${newToken}`
              }
              resolve(request<T>(path, { ...options, headers: newHeaders }))
            })
          })
        }
      } else {
        tokenStorage.remove()
        tokenStorage.removeRefreshToken()
        window.location.href = '/login'
      }
    } else if (response.status === 401 && (path === '/auth/refresh' || window.location.pathname !== '/login')) {
      tokenStorage.remove()
      tokenStorage.removeRefreshToken()
      window.location.href = '/login'
    }

    const errorMessage = (error as { message?: string; error?: string }).error ?? (error as { message?: string }).message ?? 'Request failed'
    throw new ApiError(errorMessage, response.status, (error as { code?: string }).code, (error as { details?: any }).details)
  }

  // 204 No Content — retorna undefined sem tentar parsear JSON
  if (response.status === 204) return undefined as unknown as T

  if (options?.responseType === 'blob') {
    return response.blob() as unknown as Promise<T>
  }

  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { method: 'GET', ...options }),

  getBlob: (path: string, options?: RequestInit): Promise<Blob> =>
    request<Blob>(path, { method: 'GET', responseType: 'blob', ...options }),

  post: <T>(path: string, body: unknown, options?: RequestInit) => {
    const isFormData = body instanceof FormData
    return request<T>(path, { method: 'POST', body: isFormData ? body : JSON.stringify(body), ...options })
  },

  put: <T>(path: string, body: unknown, options?: RequestInit) => {
    const isFormData = body instanceof FormData
    return request<T>(path, { method: 'PUT', body: isFormData ? body : JSON.stringify(body), ...options })
  },

  patch: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body), ...options }),

  delete: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { method: 'DELETE', ...options }),
}
